<template>
  <div
    v-if="style && options"
    :class="[
      'style-preview',
      { 'style-preview--placeholder': !name },
      { 'style-preview--rounded': rounded },
      { 'style-preview--no-text': noText }
    ]"
  >
    <div
      v-if="optionImageUrls"
      class="color-run-container"
    >
      <ColorRunPreview
        :images="optionImageUrls"
        @click-colorway="onClickColorWay"
      />
    </div>
    <div
      v-else
      class="style-preview__image-container"
    />

    <span
      v-if="!noText && name"
      class="style-preview__name"
    >{{ name }}</span>
    <span
      v-if="!noText && tags"
      class="style-preview__tags"
    >{{ tags }}</span>
    <StitchDialog
      v-if="enableImageClick"
      append-to-body
      :visible="activeImage !== null"
      @close="closeImageDialog"
    >
      <StitchZoom
        v-loading="isLoadingImage"
        :img-normal="activeImage ? activeImage.normal : null"
        :img-zoom="activeImage ? activeImage.zoom : null"
        :scale="1.5"
        @loaded="disableImageLoading()"
      />
    </StitchDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Utils from '@/services/utils'
import { imageResolutions } from '@/constants/image'

import { S3Trim } from '@/mixins/s3Trim.js'

import ColorRunPreview from '@/components/ColorRunPreview.vue'

export default {
  name: 'StylePreview',

  components: {
    ColorRunPreview
  },

  mixins: [S3Trim],

  props: {
    styleId: {
      type: Number,
      required: true
    },
    rounded: {
      type: Boolean,
      default: false
    },
    noText: {
      type: Boolean,
      default: false
    },
    imageResolution: {
      type: String,
      default: imageResolutions.STYLE_THUMBNAIL
    },
    enableImageClick: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    activeImage: null,
    isLoadingImage: true
  }),

  computed: {
    ...mapGetters([
      'getStyleById',
      'getOptionsByStyleId',
      'getDetailsByStyleId'
    ]),

    /**
     * @returns {Array}
     */
    optionImageUrls () {
      return this.options
        .filter(option => option.file)
        .map(option => {
          return this.s3Trim(option.assets_file, this.imageResolution)
        })
    },

    /**
     * @returns {object}
     */
    style () {
      return this.getStyleById(this.styleId)
    },

    /**
     * @returns {Array}
     */
    options () {
      return this.getOptionsByStyleId(this.styleId)
    },

    /**
     * @returns {string}
     */
    name () {
      return this.style.name
    },

    /**
     * @returns {string}
     */
    tags () {
      return Utils.parseStyleMetaNames(this.style).join(' / ')
    }
  },

  methods: {
    /**
     */
    disableImageLoading () {
      this.isLoadingImage = false
    },

    /**
     */
    closeImageDialog () {
      this.activeImage = null
      this.isLoadingImage = true
    },

    /**
     * @param {number} imageIndex
     */
    onClickColorWay (imageIndex) {
      const fullImageUrl = this.options[imageIndex].assets_file || null
      this.activeImage = {
        normal: this.s3Trim(fullImageUrl, this.imageResolution),
        zoom: this.s3Trim(fullImageUrl, imageResolutions.ZOOM)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$min-height-style-preview: 80px;
$size-placeholder: 100px;
$min-height-style-name: 18px;
$ipad-style-preview-image-height: 85%;
$style-preview-span-margin: 2px;
$min-height-zoom-hover: 65vh;

.style-preview {
  @include size(100%);

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: $spacer-unit;
  overflow: hidden;
  text-align: center;
  background-color: $white;

  &.style-preview--placeholder {
    background-color: $blue-light;
    border: 0;

    * {
      display: none;
    }
  }

  &.style-preview--rounded {
    border-radius: $border-radius-default;
  }

  &.style-preview--no-text {
    padding: $spacer-unit;

    *:last-of-type {
      margin-bottom: 0;
    }
  }

  .style-preview__image-container {
    @include size(100%);

    margin-bottom: $spacer-unit;
    background-image: url('../../assets/images/shirt-placeholder.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    @include respond-up-to('m') {
      height: $ipad-style-preview-image-height;
    }
  }

  span {
    min-height: $min-height-style-name;
    margin-bottom: $spacer-unit;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

.style-preview__image {
  align-self: center;
  max-height: 100%;
}

.style-preview__name {
  @include text-ellipsis(100%);

  color: $grey;
  text-transform: uppercase;
}

.style-preview__tags {
  font-size: $font-size-medium;

  @include respond-up-to('m') {
    line-height: $font-size-medium;
  }
}

img {
  pointer-events: none;
}

.color-run-container {
  flex: 1 1 auto;
  width: 100%;
  height: 0;
  padding: $spacer-unit-x2;
}

.style-preview--no-text {
  .color-run-container {
    padding: 0;
  }
}

/deep/ .color-run-preview__image {
  cursor: pointer;
}

/deep/ .zoom-on-hover {
  display: inherit;
  min-height: $min-height-zoom-hover;
}

@media only screen and (max-height: $max-height-list-item) {
  .color-run-container {
    padding: $font-size-medium;
  }

  .style-preview span {
    min-height: $font-size-medium;
    margin-bottom: $style-preview-span-margin;
    font-size: $font-size-small;
    line-height: 1;
  }
}

@media only screen and (max-height: $height-list-item *2) {
  .color-run-container {
    padding: $spacer-unit;
  }

  .style-preview span {
    display: none;
  }
}
</style>
